import React, { useCallback, useEffect } from 'react';
import { secureStorage } from 'utils/secureStorage';
import { Stack } from 'react-bootstrap';
import TopStories from 'components/TopStories';
import CreatePost from 'components/CreatePost';
import SocialPost from 'components/SocialPost';
import { useGetPostQuery, useGetStaticPostQuery, usePageVisitMutation } from 'services';
import useFeedsStore, {
	setCurrentCursor,
	setFeeds,
	setFeedsMeta,
	setLastcursor,
	setLastFeedPage,
	setLastScrolling,
} from 'store/feeds';
import { dataLimits } from 'constants/index';
import InfiniteScroll from 'react-infinite-scroll-component';
import AdvertisementPost from 'components/SocialPost/AdvertisementPost';
import useAuthStore from 'store/auth';
import useTranslate from 'hooks/useTranslate';
import './styles.scss';
import { SocialPostSkeletonList } from 'components/Common/Skeleton/SocialPostSkeleton';

const InteractiveDemo: React.FC = () => {
	const { translate } = useTranslate();
	const {
		feeds: feedsList = [],
		feedsMeta,
		lastScrolling,
		lastcursor,
		currentCusror,
	}: Record<string, any> = useFeedsStore((state) => state);

	const { token: isAuthenticated }: Record<string, any> = useAuthStore(
		(state) => state.userInfo ?? {},
	);
	secureStorage.setItem('pageVisit', 'home_b');
	const { mutateAsync: pageVisitMutation } = usePageVisitMutation();

	const searchString = `order=DESC&cursor=${currentCusror || 0}&limit=${dataLimits?.feedsListing}&hasExpired=false&inActive=false&type=all&socialPostType=all&connectionType=connected`;

	const {
		data: { docs: postList = {}, meta = {} } = {},
		status,
		isStale,
		refetch,
		isLoading,
	} = isAuthenticated
		? useGetPostQuery({
				searchString: searchString,
				userToken: isAuthenticated,
				isFetch: lastcursor === currentCusror ? false : true,
			})
		: useGetStaticPostQuery({
				searchString: searchString,
				isFetch: lastcursor === currentCusror ? false : true,
			});

	useEffect(() => {
		if (typeof refetch === 'function') {
			refetch();
		}
	}, [isAuthenticated]);

	useEffect(() => {
		const updateIp = () => {
			const ipAddress = secureStorage.getItem('ipAddress') ?? '';
			if (!ipAddress) return;

			pageVisitMutation({ type: 'startpage_b', ipAddress });
		};
		updateIp();

		// Listen to custom event triggered in App.tsx
		window.addEventListener('ipAddressChanged', updateIp);

		return () => {
			window.removeEventListener('ipAddressChanged', updateIp);
		};
	}, [pageVisitMutation]);

	const loadMoreFunc = useCallback(() => {
		setLastcursor(0);
	}, [feedsMeta]);

	const handleScroll = (event: any) => {
		const scrollTop = event.target.scrollTop;
		setLastScrolling(scrollTop);
	};

	useEffect(() => {
		if (
			status === 'success' &&
			postList &&
			Array.isArray(postList.results) &&
			postList.results.length > 0 &&
			isStale
		) {
			setFeeds([...feedsList, ...postList.results]);
			setFeedsMeta(meta);
			setCurrentCursor(meta?.nextCursor || 0);
			setLastcursor(meta?.nextCursor || 0);
		}
	}, [postList, status]);

	useEffect(() => {
		const scrollableDiv = document.getElementById('infiniteDiv-social-feeds');
		if (scrollableDiv) {
			scrollableDiv.scrollTop = lastScrolling;
		}
	}, [lastScrolling, feedsList.length]);

	const refreshFeeds = () => {
		refetch().then((res: any) => {
			if (res?.isSuccess) {
				setFeeds(res?.data?.docs?.results);
				setLastFeedPage(res?.data?.meta?.page || 1);
				setCurrentCursor(res?.data?.meta?.nextCursor || 0);
				setLastcursor(res?.data?.meta?.nextCursor || 0);
			}
		});
	};

	return (
		<div className="social-feed">
			<div className="social-feed-left">
				<Stack className="social-feed-posts">
					<TopStories />
					<CreatePost />
					{feedsList?.length > 0 ? (
						<InfiniteScroll
							dataLength={feedsList.length}
							next={loadMoreFunc}
							hasMore={feedsMeta?.hasMore}
							loader={feedsList.length >= 1 && <SocialPostSkeletonList count={3} noButton={true} />}
							scrollableTarget={`infiniteDiv-social-feeds right-area`}
							scrollThreshold="300px"
							refreshFunction={loadMoreFunc}
							pullDownToRefreshThreshold={50}
							onScroll={handleScroll}
							pullDownToRefreshContent={
								<h3 style={{ textAlign: 'center' }}>
									&#8595; {translate('Common.pullDownToRefresh')}
								</h3>
							}
							releaseToRefreshContent={
								<h3 style={{ textAlign: 'center' }}>
									&#8593; {translate('Common.releaseToRefresh')}{' '}
								</h3>
							}
						>
							{feedsList.length > 0 ? (
								feedsList.map((post: any, index: number) => (
									<React.Fragment key={`${post?.postId}-${index}-${post?.userId}`}>
										{post?.isAdvertisement ? (
											<AdvertisementPost
												post={post}
												key={`${post?.postId}-${index}-${post?.userId}`}
											/>
										) : (
											<SocialPost
												post={post}
												key={`${post?.postId}-${index}-${post?.userId}`}
												refetch={refreshFeeds}
											/>
										)}
									</React.Fragment>
								))
							) : (
								<Stack className="fw-bold">{translate('Common.noPostFound')}</Stack>
							)}
						</InfiniteScroll>
					) : (
						<>
							{isLoading ? (
								<SocialPostSkeletonList count={3} noButton={true} />
							) : (
								<Stack className="fw-bold">{translate('Common.noPostFound')}</Stack>
							)}
						</>
					)}
				</Stack>
			</div>
		</div>
	);
};

export default InteractiveDemo;
